import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import LinkIcon from '@mui/icons-material/Link';
import { gridDimensionsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';

/**
 * Withdrawals ("herroepingen", EU 2023/2673, 2026-10-13) on the Data tab:
 * the expandable row and the two row actions. The rows are evidence, so
 * nothing is edited or deleted — only
 *  - mark as processed (mini-api POST /withdrawals/{id}/processed);
 *  - link to an order (POST /withdrawals/{id}/link { order }) when the
 *    storefront could not match the number the customer typed.
 * The return and the refund themselves happen in the POS / payment provider.
 * `proxy(method, path, body)` is the host screen's auth-proxy call.
 */

const tro = (t) => (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
const errorOf = (r, fb) => r?.data?.body?.message || r?.message || fb;

/** "2026-10-13 14:05:09" → "13/10/2026 14:05"; "2026-10-01" → "01/10/2026". */
export const fmtWdDate = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(v || ''));
    if (!m) return v ? String(v) : '';
    return `${m[3]}/${m[2]}/${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ''}`;
};

export const isWdLinked = (row) => !!row && Number(row.reservation_id) > 0;
export const isWdNew = (row) => !!row && String(row.status || '').toUpperCase() === 'NEW';

const who = (row) => (row ? [row.firstname, row.lastname].filter(Boolean).join(' ') : '');
const Summary = ({ row }) => (row ? (
    <Typography variant="body2">
        {[who(row), row.email, fmtWdDate(row.created_at)].filter(Boolean).join(' · ')}
    </Typography>
) : null);
Summary.propTypes = { row: PropTypes.object };

/** Mark a withdrawal as processed (after the return + refund in the POS / payment provider). */
export const WithdrawalProcessDialog = ({ open, onClose, t, proxy, row, onDone }) => {
    const tr = tro(t);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setBusy(false);
        setDone(false);
        setError('');
    }, [open, row]);

    const go = async () => {
        setBusy(true);
        setError('');
        try {
            const r = await proxy('POST', `/withdrawals/${row.id}/processed`);
            if (r && r.success) {
                setDone(true);
                if (onDone) onDone(r.data);
            } else {
                setError(errorOf(r, tr('cfg_wd_process_failed', 'Markeren als verwerkt is mislukt.')));
            }
        } catch (e) {
            setError(e?.message || tr('cfg_wd_process_failed', 'Markeren als verwerkt is mislukt.'));
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-wd-process-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_wd_process_title', 'Herroeping als verwerkt markeren')}{row ? ` · #${row.id}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Summary row={row} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_wd_process_desc', 'Markeer de herroeping als verwerkt zodra de artikelen terug zijn en de klant terugbetaald is. Het retour en de terugbetaling zelf gebeuren niet hier, maar in de kassa en bij de betaalprovider. De herroeping blijft als bewijs bewaard en kan niet gewijzigd of verwijderd worden.')}
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                    {done && <Alert severity="success" data-wd-process-done>{tr('cfg_wd_process_ok', 'De herroeping is als verwerkt gemarkeerd.')}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{done ? tr('cfg_act_close', 'Sluiten') : tr('cfg_act_cancel', 'Annuleren')}</Button>
                {!done && (
                    <Button variant="contained" onClick={go} disabled={busy || !row} sx={{ textTransform: 'none' }}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <TaskAltIcon />}>
                        {tr('cfg_wd_process_btn', 'Markeren als verwerkt')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
WithdrawalProcessDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, proxy: PropTypes.func,
    row: PropTypes.object, onDone: PropTypes.func,
};

/** Link (or re-link) a withdrawal to an order by its number. */
export const WithdrawalLinkDialog = ({ open, onClose, t, proxy, row, onDone }) => {
    const tr = tro(t);
    const [number, setNumber] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);       // the updated row
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        // start from what the customer typed (often right, with a typo at most)
        setNumber(isWdLinked(row) ? String(row?.order_reference || '') : String(row?.order_number || ''));
        setBusy(false);
        setDone(null);
        setError('');
    }, [open, row]);

    const clean = number.trim().replace(/^#/, '').replace(/\s+/g, '');
    const bad = clean !== '' && !/^\d+$/.test(clean);

    const go = async () => {
        setBusy(true);
        setError('');
        try {
            const r = await proxy('POST', `/withdrawals/${row.id}/link`, { order: clean });
            if (r && r.success) {
                setDone(r.data || {});
                if (onDone) onDone(r.data);
            } else {
                setError(errorOf(r, tr('cfg_wd_link_failed', 'Koppelen is mislukt.')));
            }
        } catch (e) {
            setError(e?.message || tr('cfg_wd_link_failed', 'Koppelen is mislukt.'));
        }
        setBusy(false);
    };

    const period = done ? Number(done.within_period) : null;
    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-wd-link-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_wd_link_title', 'Koppelen aan bestelling')}{row ? ` · #${row.id}` : ''}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Summary row={row} />
                    {row && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {tr('cfg_wd_link_typed', 'Ingevuld door de klant')}: {row.order_number || '—'}
                            {row.order_date ? ` · ${tr('cfg_wd_col_order_date', 'Besteld op')} ${fmtWdDate(row.order_date)}` : ''}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tr('cfg_wd_link_desc', 'Vul het bestelnummer in: het nummer uit de bevestigingsmail van de klant, of het webshopnummer. De termijn wordt dan berekend vanaf de datum van die bestelling. Een verkeerde koppeling kan je zo ook rechtzetten.')}
                    </Typography>
                    {!done && (
                        <TextField size="small" fullWidth autoFocus value={number} onChange={(e) => setNumber(e.target.value)}
                            label={tr('cfg_wd_link_field', 'Bestelnummer')} error={bad}
                            helperText={bad ? tr('cfg_wd_link_digits', 'Enkel cijfers.') : ' '}
                            onKeyDown={(e) => { if (e.key === 'Enter' && clean && !bad && !busy) go(); }}
                            inputProps={{ 'data-wd-link-number': true, inputMode: 'numeric' }} />
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                    {done && (
                        <Alert severity={period === 0 ? 'warning' : 'success'} data-wd-link-done>
                            {tr('cfg_wd_link_ok', 'Gekoppeld aan bestelling')} {done.order_reference || clean}
                            {' — '}
                            {period === 1 ? tr('cfg_wd_period_in', 'Binnen termijn') : period === 0 ? tr('cfg_wd_period_out', 'Buiten termijn') : tr('cfg_wd_period_unknown', 'Onbekend')}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{done ? tr('cfg_act_close', 'Sluiten') : tr('cfg_act_cancel', 'Annuleren')}</Button>
                {!done && (
                    <Button variant="contained" onClick={go} disabled={busy || !row || !clean || bad} sx={{ textTransform: 'none' }}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}>
                        {tr('cfg_wd_link_btn', 'Koppelen')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
WithdrawalLinkDialog.propTypes = WithdrawalProcessDialog.propTypes;

// ── Expandable row ──────────────────────────────────────────────────────

// per webshop + withdrawal + last change, so re-expanding doesn't refetch
// but an action (which bumps updated_at) does
const _cache = new Map();

// The detail panel spans all columns: pin it to the visible grid width.
const VisibleWidth = ({ children }) => {
    const apiRef = useGridApiContext();
    const dims = useGridSelector(apiRef, gridDimensionsSelector);
    const width = dims?.viewportInnerSize?.width;
    return <Box sx={{ position: 'sticky', left: 0, width: width || '100%', boxSizing: 'border-box' }}>{children}</Box>;
};
VisibleWidth.propTypes = { children: PropTypes.node };

const WithdrawalDetailBody = ({ row, proxy, t, cacheKey }) => {
    const tr = tro(t);
    const key = `${cacheKey}:${row.id}:${row.updated_at || ''}`;
    const [data, setData] = useState(() => _cache.get(key) || null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (_cache.has(key)) { setData(_cache.get(key)); return undefined; }
        let alive = true;
        setError('');
        proxy('GET', `/withdrawals/${row.id}`)
            .then((r) => {
                if (!alive) return;
                if (r && r.success && r.data) {
                    _cache.set(key, r.data);
                    setData(r.data);
                } else {
                    setError(errorOf(r, '-'));
                }
            })
            .catch((e) => { if (alive) setError(e?.message || '-'); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    if (error) {
        return (
            <Box sx={{ px: 2, py: 1 }}>
                <Alert severity="warning" sx={{ py: 0 }}>
                    {tr('cfg_wd_det_failed', 'De details konden niet geladen worden.')}
                    {error !== '-' && <Typography variant="caption" component="div" sx={{ opacity: 0.75 }}>{error}</Typography>}
                </Alert>
            </Box>
        );
    }
    if (!data) return <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>;

    const items = Array.isArray(data.items) ? data.items : [];
    const flags = [
        ['mail_customer_sent', tr('cfg_wd_det_mail_customer', 'Ontvangstbevestiging naar klant')],
        ['mail_shop_sent', tr('cfg_wd_det_mail_shop', 'Melding naar winkel')],
    ];
    const meta = [
        data.language ? `${tr('cfg_data_col_lang', 'Taal')}: ${String(data.language).toUpperCase()}` : '',
        data.order_created_at ? `${tr('cfg_wd_det_order_created', 'Bestelling aangemaakt')}: ${fmtWdDate(data.order_created_at)}` : '',
        data.period_days ? `${tr('cfg_wd_det_period_days', 'Termijn (dagen na bestelling, incl. levering)')}: ${data.period_days}` : '',
        data.linked_at ? `${tr('cfg_wd_det_linked', 'Gekoppeld')}: ${fmtWdDate(data.linked_at)}${data.linked_by ? ` · ${data.linked_by}` : ''}` : '',
        data.processed_at ? `${tr('cfg_wd_status_processed', 'Verwerkt')}: ${fmtWdDate(data.processed_at)}${data.processed_by ? ` · ${data.processed_by}` : ''}` : '',
    ].filter(Boolean);
    const textSx = { fontSize: 12.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

    return (
        <Box sx={{ px: 1.5, pl: 6, pt: 0.75, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }} data-wd-detail={row.id}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {flags.map(([f, label]) => (
                    <Chip key={f} size="small" sx={{ height: 20, fontSize: 11 }}
                        label={`${label}: ${Number(data[f]) === 1 ? tr('cfg_wd_det_sent', 'verstuurd') : tr('cfg_wd_det_not_sent', 'niet verstuurd')}`}
                        color={Number(data[f]) === 1 ? 'success' : 'warning'}
                        variant={Number(data[f]) === 1 ? 'filled' : 'outlined'} />
                ))}
                {meta.map(m => <Typography key={m} variant="caption" sx={{ color: 'text.secondary', ml: 0.75 }}>{m}</Typography>)}
            </Box>
            {items.length > 0 ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.25, overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.25, px: 0.75, fontSize: 12, whiteSpace: 'nowrap' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{tr('cfg_wd_det_col_article', 'Artikel')}</TableCell>
                                <TableCell>{tr('cfg_wd_det_col_ref', 'Referentie')}</TableCell>
                                <TableCell>{tr('cfg_wd_det_col_size', 'Maat')}</TableCell>
                                <TableCell>{tr('cfg_wd_det_col_color', 'Kleur')}</TableCell>
                                <TableCell align="right">{tr('cfg_wd_det_col_qty', 'Aantal')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((it, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ whiteSpace: 'normal !important' }}>{it.name || it.article || '—'}</TableCell>
                                    <TableCell>{it.reference || '—'}</TableCell>
                                    <TableCell>{it.size || '—'}</TableCell>
                                    <TableCell>{it.color || '—'}</TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {it.quantity ?? '—'}
                                        {it.ordered_quantity != null && Number(it.ordered_quantity) !== Number(it.quantity) ? ` / ${it.ordered_quantity}` : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ) : (
                <Typography sx={textSx}>
                    <strong>{tr('cfg_wd_det_items_text', 'Omschrijving van de klant')}:</strong> {data.items_text || '—'}
                </Typography>
            )}
            {data.remark && (
                <Typography sx={textSx}><strong>{tr('cfg_wd_det_remark', 'Opmerking')}:</strong> {data.remark}</Typography>
            )}
        </Box>
    );
};
WithdrawalDetailBody.propTypes = {
    row:      PropTypes.object.isRequired,
    proxy:    PropTypes.func.isRequired,
    t:        PropTypes.func,
    cacheKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const WithdrawalDetailPanel = (props) => <VisibleWidth><WithdrawalDetailBody {...props} /></VisibleWidth>;
WithdrawalDetailPanel.propTypes = WithdrawalDetailBody.propTypes;
